import type { OrganizationRecord } from '../models';
import './OrganizationOverviewCard.css';

interface OrganizationOverviewCardProps {
  organization: OrganizationRecord;
  /** Opens the setup wizard read-only, every field disabled. */
  onView: () => void;
  /** Opens the setup wizard on step 1, editable. */
  onEdit: () => void;
}

/** The status's tone: green when Active, amber otherwise. */
function statusTone(status: string | null | undefined): string {
  return status?.trim().toLowerCase() === 'active' ? 'active' : 'other';
}

/**
 * The status as the card words it. An organization turns Active when its setup is completed and sits at
 * Pending until then, so the card says what that means for the setup rather than repeating the raw status.
 * Any other status is shown as the platform names it.
 */
function statusLabel(status: string): string {
  switch (status.trim().toLowerCase()) {
    case 'active':
      return 'Setup Completed';
    case 'pending':
      return 'Setup Pending';
    default:
      return status;
  }
}

/**
 * The organization a Mega Admin has provisioned, as one card on their dashboard.
 *
 * Drawn the way the setup's package card is (`dabhoardcard.png`): a tinted, bordered card with a check in
 * the corner, a white tile holding the logo, the name, its descriptive lines, a rule, and the status below
 * it. The lines are this organization's own -- code, location and package, then its entity, business
 * unit, division and department counts from `GET /organizations/{code}` -- and View and Edit sit beside the
 * status as the two ways back into the setup.
 */
export function OrganizationOverviewCard({ organization, onView, onEdit }: OrganizationOverviewCardProps) {
  const name = organization.organizationName || organization.organizationCode;
  const location = [organization.cityName, organization.countryName].filter(Boolean).join(', ');
  /* From `GET /organizations/{code}`'s `counts`; a dash where the response carries none. */
  const counts = organization.counts;

  const facts: readonly { label: string; value: number | undefined }[] = [
    { label: 'Entity', value: counts?.entities },
    { label: 'Business Unit', value: counts?.businessUnits },
    { label: 'Division', value: counts?.divisions },
    { label: 'Department', value: counts?.departments },
  ];

  return (
    <article className="org-card" aria-label={`Organization ${name}`}>
      <span className="org-card__check" aria-hidden="true">
        <i className="pi pi-check" aria-hidden="true" />
      </span>

      <div className="org-card__art" aria-hidden="true">
        {organization.orgImage ? (
          <img className="org-card__image" src={organization.orgImage} alt="" />
        ) : (
          <i className="pi pi-building" aria-hidden="true" />
        )}
      </div>

      <h3 className="org-card__name">{name}</h3>

      <ul className="org-card__lines">
        <li>
          <i className="pi pi-id-card" aria-hidden="true" />
          <span className="org-card__code">{organization.organizationCode}</span>
        </li>
        {location ? (
          <li>
            <i className="pi pi-map-marker" aria-hidden="true" />
            {location}
          </li>
        ) : null}
        {organization.packageName ? (
          <li>
            <i className="pi pi-box" aria-hidden="true" />
            {organization.packageName} package
          </li>
        ) : null}
      </ul>

      <dl className="org-card__facts">
        {facts.map((fact) => (
          <div className="org-card__fact" key={fact.label}>
            <dt>{fact.label}</dt>
            <dd>{fact.value ?? '—'}</dd>
          </div>
        ))}
      </dl>

      <div className="org-card__line" aria-hidden="true" />

      <div className="org-card__footer">
        {organization.statusName ? (
          <span className={`org-card__status org-card__status--${statusTone(organization.statusName)}`}>
            {statusLabel(organization.statusName)}
          </span>
        ) : (
          <span />
        )}

        <div className="org-card__actions">
          <button
            type="button"
            className="org-card__action"
            aria-label={`View ${name}`}
            title="View"
            onClick={onView}
          >
            <i className="pi pi-eye" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="org-card__action"
            aria-label={`Edit ${name}`}
            title="Edit"
            onClick={onEdit}
          >
            <i className="pi pi-pencil" aria-hidden="true" />
          </button>
        </div>
      </div>
    </article>
  );
}
